import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Search, Users, Ship, CheckCircle2, AlertTriangle, Layers,
} from "lucide-react";
import { api } from "../lib/api";
import FormError from "./ui/FormError";
import useEscClose from "../hooks/useEscClose";

/**
 * BulkAssignSquadClassModal — Excel-like inline spreadsheet for Squad
 * + Class assignment. Every cell edit auto-saves (debounced 500ms)
 * with a per-row status chip (idle → saving → saved → error).
 *
 * Design choices:
 *  1. No global "Save" or "changes pending" — the moment a coach picks
 *     a squad or adds a class chip, we POST just that one row to
 *     `/sailors/bulk-assign` and update local state on success.
 *  2. Debounce is per-row so rapid chip edits collapse into one save.
 *  3. Row status column shows: nothing (idle) → tiny spinner (saving)
 *     → green ✓ that fades after 2s (saved) → red ⚠ with tooltip
 *     (error, kept until next successful save).
 *  4. Class options remain a union of Classes-Master + every current
 *     `subgroup`, so newly-typed classes appear in the dropdown after
 *     save without needing an admin master edit.
 */
export default function BulkAssignSquadClassModal({ open, onClose, onSaved }) {
  const [sailors, setSailors] = useState([]);
  const [squads, setSquads] = useState([]);
  const [classes, setClasses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [squadFilter, setSquadFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  // Row-level autosave status: sailorId → { status, message?, ts }
  const [rowState, setRowState] = useState({});
  // Optimistic display state so the UI updates immediately while the
  // debounce timer waits.  sailorId → { group, subgroup }
  const [pending, setPending] = useState({});
  // Refs mirror state so the debounced runSave() always reads the LATEST
  // values even though setTimeout captured its closure 500ms earlier.
  const pendingRef = useRef(pending);
  const sailorsRef = useRef(sailors);
  useEffect(() => { pendingRef.current = pending; }, [pending]);
  useEffect(() => { sailorsRef.current = sailors; }, [sailors]);
  // Per-row debounce handles so we can coalesce rapid edits.
  const timers = useRef({});
  // Track how many autosaves are in-flight so we can decide when to
  // notify the parent (which triggers a global sidebar refresh).
  const inFlight = useRef(0);

  useEscClose(open, onClose);

  // Load roster + master lists on open. Clear all local scratch state
  // so re-opening the modal doesn't show stale "saved!" chips.
  useEffect(() => {
    if (!open) return;
    setError("");
    setPending({});
    setRowState({});
    Object.values(timers.current).forEach((t) => clearTimeout(t));
    timers.current = {};
    inFlight.current = 0;
    setSearch("");
    setSquadFilter("all");
    setLevelFilter("all");
    setLoading(true);
    Promise.all([
      api.get("/sailors").then((r) => r.data || []),
      api.get("/squads").then((r) => r.data || []),
      api.get("/classes").then((r) => r.data || []),
      api.get("/levels").then((r) => r.data || []),
    ])
      .then(([s, sq, cl, lv]) => { setSailors(s); setSquads(sq); setClasses(cl); setLevels(lv); })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load data"))
      .finally(() => setLoading(false));
  }, [open]);

  const squadOptions = useMemo(() => {
    const set = new Set();
    squads.forEach((s) => { if (s.name) set.add(s.name); });
    sailors.forEach((s) => { if (s.group) set.add(s.group); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [squads, sailors]);

  const classOptions = useMemo(() => {
    // Only Classes Master entries populate the autocomplete dropdown —
    // legacy free-text subgroups on athletes are still displayed as
    // chips on their own rows, they just don't leak into every other
    // row's suggestion list. Admins clean up legacy values via the
    // Classes Master modal.
    return classes
      .filter((c) => c.name && c.active !== false)
      .map((c) => c.name)
      .sort((a, b) => a.localeCompare(b));
  }, [classes]);

  const levelOptions = useMemo(() => {
    const set = new Set();
    levels.forEach((l) => { if (l.name && l.active !== false) set.add(l.name); });
    // Preserve any legacy level values already on athletes so admins
    // can migrate rather than get their existing tier stripped out.
    sailors.forEach((s) => { if (s.level) set.add(s.level); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [levels, sailors]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sailors.filter((s) => {
      const currentGroup = pending[s.id]?.group ?? s.group ?? "";
      const currentLevel = pending[s.id]?.level === "__CLEAR__"
        ? ""
        : pending[s.id]?.level ?? s.level ?? "";
      if (squadFilter !== "all" && currentGroup !== squadFilter) return false;
      if (levelFilter !== "all") {
        if (levelFilter === "__unset__" && currentLevel) return false;
        if (levelFilter !== "__unset__" && currentLevel !== levelFilter) return false;
      }
      if (q && !s.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sailors, pending, search, squadFilter, levelFilter]);

  // -------- Autosave engine ---------------------------------------
  const scheduleSave = (sailorId) => {
    if (timers.current[sailorId]) clearTimeout(timers.current[sailorId]);
    timers.current[sailorId] = setTimeout(() => {
      timers.current[sailorId] = null;
      runSave(sailorId);
    }, 500);
  };

  const runSave = async (sailorId) => {
    const s = sailorsRef.current.find((x) => x.id === sailorId);
    if (!s) return;
    const p = pendingRef.current[sailorId];
    if (!p) return;
    const groupChanged = p.group !== undefined && (p.group || "") !== (s.group || "");
    const subChanged =
      p.subgroup !== undefined &&
      (p.subgroup === "__CLEAR__"
        ? Boolean(s.subgroup)
        : (p.subgroup || "") !== (s.subgroup || ""));
    const levelChanged =
      p.level !== undefined &&
      (p.level === "__CLEAR__"
        ? Boolean(s.level)
        : (p.level || "") !== (s.level || ""));
    if (!groupChanged && !subChanged && !levelChanged) {
      // Nothing actually diffs from server state — clear scratch entry
      setPending((prev) => { const n = { ...prev }; delete n[sailorId]; return n; });
      return;
    }
    const payload = {
      sailor_id: sailorId,
      group: groupChanged ? p.group : null,
      subgroup: subChanged && p.subgroup !== "__CLEAR__" ? p.subgroup : null,
      clear_subgroup: subChanged && p.subgroup === "__CLEAR__",
      level: levelChanged && p.level !== "__CLEAR__" ? p.level : null,
      clear_level: levelChanged && p.level === "__CLEAR__",
    };
    setRowState((r) => ({ ...r, [sailorId]: { status: "saving", ts: Date.now() } }));
    inFlight.current += 1;
    try {
      const r = await api.post("/sailors/bulk-assign", { rows: [payload] });
      // Update local sailor snapshot with the values the server just
      // canonicalised (dedup / trim / clear) so future diffs compute right.
      const updatedInfo = (r.data?.updated || [])[0];
      setSailors((prev) => prev.map((row) => row.id === sailorId ? {
        ...row,
        group: groupChanged ? p.group : row.group,
        subgroup: subChanged && p.subgroup === "__CLEAR__"
          ? null
          : (subChanged ? p.subgroup : row.subgroup),
        level: levelChanged && p.level === "__CLEAR__"
          ? null
          : (levelChanged ? p.level : row.level),
      } : row));
      setPending((prev) => { const n = { ...prev }; delete n[sailorId]; return n; });
      setRowState((rs) => ({ ...rs, [sailorId]: { status: "saved", ts: Date.now(), name: updatedInfo?.name } }));
      // Fade the "saved" chip after 2s.
      setTimeout(() => {
        setRowState((rs) => {
          const cur = rs[sailorId];
          if (!cur || cur.status !== "saved") return rs;
          const n = { ...rs }; delete n[sailorId]; return n;
        });
      }, 2000);
    } catch (e) {
      const message = e?.response?.data?.detail || e.message || "Save failed";
      setRowState((rs) => ({ ...rs, [sailorId]: { status: "error", ts: Date.now(), message } }));
    } finally {
      inFlight.current -= 1;
      if (inFlight.current === 0) onSaved?.();
    }
  };

  // -------- Local edit setters (write scratch → schedule save) -----
  const setGroup = (id, value) => {
    setPending((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), group: value } }));
    scheduleSave(id);
  };
  const setChips = (id, chips) => {
    if (chips.length === 0) {
      setPending((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), subgroup: "__CLEAR__" } }));
    } else {
      const joined = chips.join(", ").slice(0, 128);
      setPending((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), subgroup: joined } }));
    }
    scheduleSave(id);
  };
  const setLevel = (id, value) => {
    // Empty string → explicit clear; anything else → replace
    const payload = value === "" ? "__CLEAR__" : value;
    setPending((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), level: payload } }));
    scheduleSave(id);
  };

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto py-6"
      style={{ backdropFilter: "blur(6px)", background: "rgba(11,22,39,0.72)" }}
      onClick={onClose}
      data-testid="bulk-assign-modal"
    >
      <div className="card w-full max-w-7xl mx-4 p-5 md:p-6 relative" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <Layers size={11} /> Squad, Level &amp; Class assignment
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">Sailor-wise spreadsheet</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Edits save automatically as you type — no Save button needed.
            </div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="bulk-assign-close"><X size={14} /></button>
        </div>

        {error && <div className="mb-3"><FormError message={error} onDismiss={() => setError("")} testId="bulk-assign-error" /></div>}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search athlete…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 pr-3 py-1.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60"
              data-testid="bulk-assign-search"
            />
          </div>
          <select
            value={squadFilter}
            onChange={(e) => setSquadFilter(e.target.value)}
            className="py-1.5 px-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-[11px] text-slate-200"
            data-testid="bulk-assign-squad-filter"
          >
            <option value="all">All squads</option>
            {squadOptions.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="py-1.5 px-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-[11px] text-slate-200"
            data-testid="bulk-assign-level-filter"
          >
            <option value="all">All levels</option>
            <option value="__unset__">— No level yet —</option>
            {levelOptions.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <div className="ml-auto text-[11px] text-slate-500 font-mono-data">
            <Users size={11} className="inline mr-1" />
            {filtered.length} of {sailors.length}
          </div>
        </div>

        {/* Grid */}
        <div className="rounded-lg border border-[#1E304B] max-h-[62vh] overflow-auto">
          {loading ? (
            <div className="py-10 text-center text-slate-400 text-[12px]">
              <Loader2 className="animate-spin inline" size={14} /> Loading roster…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-[11px]">No athletes match.</div>
          ) : (
            <table className="w-full text-[12px]" data-testid="bulk-assign-table">
              <thead className="sticky top-0 bg-[#0B1627] z-10">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-3 py-2 border-b border-[#1E304B]">Athlete</th>
                  <th className="text-left px-3 py-2 border-b border-[#1E304B]">
                    <span className="flex items-center gap-1"><Users size={10} /> Squad</span>
                  </th>
                  <th className="text-left px-3 py-2 border-b border-[#1E304B]">
                    <span className="flex items-center gap-1"><Layers size={10} /> Level</span>
                  </th>
                  <th className="text-left px-3 py-2 border-b border-[#1E304B]">
                    <span className="flex items-center gap-1"><Ship size={10} /> Class</span>
                  </th>
                  <th className="px-2 py-2 border-b border-[#1E304B] w-16 text-right" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const p = pending[s.id];
                  const currentGroup = p?.group !== undefined ? p.group : (s.group || "");
                  const currentSubRaw = p?.subgroup === "__CLEAR__"
                    ? ""
                    : p?.subgroup !== undefined ? p.subgroup : (s.subgroup || "");
                  const currentChips = parseChips(currentSubRaw);
                  const currentLevel = p?.level === "__CLEAR__"
                    ? ""
                    : p?.level !== undefined ? p.level : (s.level || "");
                  const state = rowState[s.id];
                  const listId = `class-datalist-${s.id}`;
                  return (
                    <tr
                      key={s.id}
                      className="border-b border-[#1E304B] last:border-b-0"
                      data-testid={`bulk-assign-row-${s.id}`}
                    >
                      <td className="px-3 py-1.5 text-slate-100 font-mono-data whitespace-nowrap align-top">
                        {s.name}
                      </td>
                      <td className="px-3 py-1.5 align-top">
                        <select
                          value={currentGroup}
                          onChange={(ev) => setGroup(s.id, ev.target.value)}
                          className="form-input !py-1 !text-[12px] w-full min-w-[160px]"
                          data-testid={`bulk-assign-group-${s.id}`}
                        >
                          {!squadOptions.includes(currentGroup) && currentGroup && (
                            <option value={currentGroup}>{currentGroup}</option>
                          )}
                          {squadOptions.map((g) => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-1.5 align-top">
                        <select
                          value={currentLevel}
                          onChange={(ev) => setLevel(s.id, ev.target.value)}
                          className="form-input !py-1 !text-[12px] w-full min-w-[140px]"
                          data-testid={`bulk-assign-level-${s.id}`}
                        >
                          <option value="">— None —</option>
                          {!levelOptions.includes(currentLevel) && currentLevel && (
                            <option value={currentLevel}>{currentLevel}</option>
                          )}
                          {levelOptions.map((l) => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-1.5 align-top">
                        <ChipInput
                          chips={currentChips}
                          onChange={(next) => setChips(s.id, next)}
                          suggestions={classOptions}
                          listId={listId}
                          testId={`bulk-assign-sub-${s.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right align-top">
                        <RowStatusChip state={state} testId={`bulk-assign-status-${s.id}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-3 text-[10px] text-slate-500 flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" /> Saved
          </span>
          <span className="flex items-center gap-1">
            <Loader2 size={9} className="animate-spin text-slate-500" /> Saving…
          </span>
          <span className="flex items-center gap-1">
            <AlertTriangle size={9} className="text-rose-400" /> Save failed — hover for details
          </span>
          <span className="ml-auto">Type a class + Enter to add a chip. Multiple classes supported.</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ------- Helpers + subcomponents ---------------------------------------

function parseChips(raw) {
  if (!raw) return [];
  const out = [];
  const seen = new Set();
  for (const tok of String(raw).split(/[,/&]/)) {
    const t = tok.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function RowStatusChip({ state, testId }) {
  if (!state) return null;
  if (state.status === "saving") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-mono-data" data-testid={testId}>
        <Loader2 size={10} className="animate-spin" /> Saving…
      </span>
    );
  }
  if (state.status === "saved") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-emerald-300 font-mono-data animate-pulse"
        data-testid={testId}
        title={state.name ? `Saved for ${state.name}` : "Saved"}
      >
        <CheckCircle2 size={11} /> Saved
      </span>
    );
  }
  if (state.status === "error") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-rose-300 font-mono-data"
        data-testid={testId}
        title={state.message}
      >
        <AlertTriangle size={11} /> Failed
      </span>
    );
  }
  return null;
}

/**
 * ChipInput — chip-based multi-select used inside each row's Class
 * cell. Selected classes render as removable pills; typing + Enter/
 * comma adds a new chip (deduped case-insensitively). A native
 * `datalist` provides autocomplete against the merged Classes-Master
 * + on-athlete class pool.
 */
function ChipInput({ chips, onChange, suggestions, listId, testId }) {
  const [draft, setDraft] = useState("");

  const filteredSuggestions = useMemo(() => {
    const picked = new Set(chips.map((c) => c.toLowerCase()));
    return suggestions.filter((c) => !picked.has(c.toLowerCase()));
  }, [chips, suggestions]);

  const commit = (raw) => {
    const cleaned = raw
      .split(/[,/&]/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (cleaned.length === 0) return;
    const lower = new Set(chips.map((c) => c.toLowerCase()));
    const next = [...chips];
    for (const c of cleaned) {
      if (!lower.has(c.toLowerCase())) {
        next.push(c);
        lower.add(c.toLowerCase());
      }
    }
    onChange(next);
    setDraft("");
  };

  const handleKey = (ev) => {
    if (ev.key === "Enter" || ev.key === ",") {
      ev.preventDefault();
      if (draft.trim()) commit(draft);
    } else if (ev.key === "Backspace" && draft === "" && chips.length > 0) {
      onChange(chips.slice(0, -1));
    }
  };

  const remove = (idx) => {
    const next = chips.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div
      className="min-w-[180px] flex flex-wrap items-center gap-1 rounded-md border border-[#1E304B] bg-[#0B1627] px-1.5 py-1 focus-within:border-cyan-400/60"
      data-testid={testId}
    >
      {chips.map((c, i) => (
        <span
          key={`${c}-${i}`}
          className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-[#122036] border border-[#1E304B] text-cyan-200/90 font-mono-data"
          data-testid={`${testId}-chip-${i}`}
        >
          {c}
          <button
            onClick={() => remove(i)}
            className="text-slate-500 hover:text-rose-300"
            title="Remove class"
            data-testid={`${testId}-chip-remove-${i}`}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        list={listId}
        maxLength={32}
        onChange={(ev) => {
          const v = ev.target.value;
          if (v.endsWith(",")) {
            commit(v.slice(0, -1));
            return;
          }
          setDraft(v);
        }}
        onKeyDown={handleKey}
        onBlur={() => { if (draft.trim()) commit(draft); }}
        placeholder={chips.length === 0 ? "—" : "add…"}
        className="flex-1 min-w-[60px] bg-transparent outline-none text-[12px] text-slate-100 placeholder-slate-500 py-0.5"
        data-testid={`${testId}-input`}
      />
      <datalist id={listId}>
        {filteredSuggestions.map((c) => <option key={c} value={c} />)}
      </datalist>
    </div>
  );
}
